import { Button, FormControl, FormErrorMessage, FormHelperText, FormLabel, Icon, useToast } from "@chakra-ui/react";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { useSaveMe } from "./useMeEditor";
import { PhoneNumberInput } from "../../../common/input/PhoneNumberInput";

interface FormValues {
  phoneNumber: PhoneNumberInput | null;
}

export const ChangePhoneNumber: React.FC = () => {
  const { me } = useLoggedInWithOrgContextState();
  const { saveMe, isSaving } = useSaveMe();
  const { t } = useTranslation();
  const displayer = useApiError();

  const toast = useToast();
  const {
    formState: { errors },
    watch,
    handleSubmit,
    control,
  } = useForm<FormValues>({
    defaultValues: {
      phoneNumber: me.phoneNumber
        ? {
            country: {
              name: me.phoneNumber.callingCode.countryName,
              alpha3Code: me.phoneNumber.callingCode.countryAlpha3Code,
              callingCode: me.phoneNumber.callingCode.value,
            },
            number: me.phoneNumber.number,
          }
        : null,
    },
  });

  const newValue = watch("phoneNumber");

  const onSubmit = async (values: FormValues) => {
    try {
      if (values.phoneNumber && values.phoneNumber.country) {
        await saveMe({
          phoneNumber: {
            value: {
              callingCode: {
                countryName: values.phoneNumber.country.name,
                countryAlpha3Code: values.phoneNumber.country.alpha3Code,
                value: values.phoneNumber.country.callingCode,
              },
              number: values.phoneNumber.number,
            },
          },
        });
      } else {
        await saveMe({
          phoneNumber: {
            value: undefined,
          },
        });
      }
      toast({ title: t("Phone number has been updated!"), status: "success" });
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const valuesAreEqual = (): boolean => {
    if (me.phoneNumber === null) {
      return newValue === null;
    } else {
      if (newValue === null) {
        return false;
      }
      return (
        me.phoneNumber?.callingCode.countryAlpha3Code === newValue.country?.alpha3Code &&
        me.phoneNumber?.callingCode.value === newValue.country?.callingCode &&
        me.phoneNumber?.number === newValue.number
      );
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl mt="15px" isInvalid={errors.phoneNumber !== undefined}>
          <FormLabel htmlFor={"phoneNumber"}>{t("Phone number")}</FormLabel>
          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              validate: (e) => {
                if (e?.country?.alpha3Code && e.country.callingCode && e.number.length > 2) {
                  return true;
                } else if (e === null) {
                  return true;
                }
                return false;
              },
            }}
            render={({ field: { onChange, value } }) => {
              return (
                <PhoneNumberInput
                  onChange={onChange}
                  onClear={() => {
                    onSubmit({ phoneNumber: null });
                  }}
                  value={value}
                />
              );
            }}
          />
          <FormErrorMessage>
            {errors.phoneNumber && t("Please provide a valid country code and phone number")}
          </FormErrorMessage>
          <FormHelperText>{t("Please provide your phone number")}</FormHelperText>
        </FormControl>
        {!valuesAreEqual() && (
          <Button
            mt="10px"
            leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
            variant={"solid"}
            colorScheme="teal"
            size={"sm"}
            isLoading={isSaving}
            type="submit"
          >
            {t("Save change")}
          </Button>
        )}
      </form>
    </>
  );
};
